/**
 * Pins Panel Component
 */

import { EventEmitter } from '../utils/events.js';
import { serial, PIN_GPIO_MAP, PIN_MODES } from '../serial/serial.js';
import { validatePwmDuty, validatePwmFrequency } from '../serial/protocol.js';
import { t } from '../utils/i18n.js';
import { notifications } from './notifications.js';

export class PinsPanel extends EventEmitter {
    constructor() {
        super();
        this._container = document.getElementById('pins-table-body');
        this._pwmFrequencyInput = document.getElementById('pwm-frequency');
        this._modes = 'IIIIIIIIII'; // Default: all inputs
        this._values = {};
        this._render();
        this._bindEvents();
    }

    _render() {
        let html = '';
        for (let pin = 1; pin <= 10; pin++) {
            const gpio = PIN_GPIO_MAP[pin];
            const availableModes = this._getAvailableModes(pin);

            html += `
                <tr data-pin="${pin}">
                    <td><span class="pin-number">${pin}</span></td>
                    <td><span class="pin-gpio">${gpio}</span></td>
                    <td>
                        <select class="mode-select" data-pin="${pin}">
                            ${availableModes.map(mode => `
                                <option value="${mode}">${mode} - ${PIN_MODES[mode].name}</option>
                            `).join('')}
                        </select>
                    </td>
                    <td>
                        <div class="value-container">
                            <span class="value-display" data-pin="${pin}">-</span>
                            <input type="text" class="value-input hidden" data-pin="${pin}" placeholder="0">
                        </div>
                    </td>
                    <td class="pin-actions">
                        <button class="btn btn-secondary btn-sm btn-set" data-pin="${pin}">${t('set')}</button>
                        <button class="btn btn-secondary btn-sm btn-read" data-pin="${pin}">${t('read')}</button>
                    </td>
                </tr>
            `;
        }
        this._container.innerHTML = html;
    }

    _getAvailableModes(pin) {
        const modes = [];
        for (const [mode, config] of Object.entries(PIN_MODES)) {
            if (config.pins.includes(pin)) {
                modes.push(mode);
            }
        }
        return modes;
    }

    _bindEvents() {
        // Mode selection
        this._container.addEventListener('change', async (e) => {
            if (e.target.classList.contains('mode-select')) {
                const pin = parseInt(e.target.dataset.pin);
                const mode = e.target.value;
                await this._handleModeChange(pin, mode);
            }
        });

        // Set button
        this._container.addEventListener('click', async (e) => {
            if (e.target.classList.contains('btn-set')) {
                const pin = parseInt(e.target.dataset.pin);
                await this._handleSetValue(pin);
            }
            if (e.target.classList.contains('btn-read')) {
                const pin = parseInt(e.target.dataset.pin);
                await this._handleReadValue(pin);
            }
        });

        // Read All button
        document.getElementById('btn-read-all')?.addEventListener('click', () => {
            this.readAllPins();
        });

        // Save to Flash button
        document.getElementById('btn-save-flash')?.addEventListener('click', async () => {
            await this.saveToFlash();
        });

        // Show input field on value display click (for settable modes)
        this._container.addEventListener('click', (e) => {
            if (e.target.classList.contains('value-display')) {
                const pin = parseInt(e.target.dataset.pin);
                const mode = this._modes[pin - 1];
                if (this._modeSupportsValueSetting(mode)) {
                    this._showValueInput(pin);
                }
            }
        });

        // Value input keyboard handling
        this._container.addEventListener('keydown', (e) => {
            if (e.target.classList.contains('value-input')) {
                if (e.key === 'Enter') {
                    e.preventDefault();
                    const pin = parseInt(e.target.dataset.pin);
                    this._handleSetValue(pin);
                } else if (e.key === 'Escape') {
                    e.preventDefault();
                    e.stopPropagation();
                    const pin = parseInt(e.target.dataset.pin);
                    // Set flag to prevent blur handler from sending
                    e.target.dataset.cancelled = 'true';
                    this._hideValueInput(pin);
                }
            }
        });

        // Value input blur - save value when focus is lost
        this._container.addEventListener('blur', async (e) => {
            if (e.target.classList.contains('value-input')) {
                // Check if user cancelled with Escape
                if (e.target.dataset.cancelled === 'true') {
                    delete e.target.dataset.cancelled;
                    return;
                }

                const pin = parseInt(e.target.dataset.pin);
                const value = e.target.value.trim();
                const mode = this._modes[pin - 1];

                // Let the common validator report malformed values as well.
                if (value) {
                    await this._sendPinValue(pin, value);
                } else {
                    // Just hide without saving if empty
                    this._hideValueInput(pin);
                }
            }
        }, true);
    }

    _showValueInput(pin) {
        const row = this._container.querySelector(`tr[data-pin="${pin}"]`);
        const display = row.querySelector('.value-display');
        const input = row.querySelector('.value-input');
        const mode = this._modes[pin - 1];

        display.classList.add('hidden');
        input.classList.remove('hidden');

        // Clear any cancelled flag
        delete input.dataset.cancelled;

        // Set default value based on mode if no value exists
        let defaultValue = this._values[pin] || '';
        if (!defaultValue || defaultValue === '-') {
            if (mode === 'O') {
                defaultValue = '0';
            } else if (mode === 'P') {
                defaultValue = '0';
            }
        }

        input.value = defaultValue;
        input.focus();
        input.select();
    }

    _hideValueInput(pin) {
        const row = this._container.querySelector(`tr[data-pin="${pin}"]`);
        const display = row.querySelector('.value-display');
        const input = row.querySelector('.value-input');

        display.classList.remove('hidden');
        input.classList.add('hidden');
    }

    async _handleModeChange(pin, newMode) {
        // Check for capture conflict (pins 4 and 5 can't both be C)
        if (newMode === 'C') {
            const otherPin = pin === 4 ? 5 : (pin === 5 ? 4 : null);
            if (otherPin && this._modes[otherPin - 1] === 'C') {
                notifications.warning(t('capture-conflict'));
                // Revert selection
                const select = this._container.querySelector(`select[data-pin="${pin}"]`);
                select.value = this._modes[pin - 1];
                return;
            }
        }

        // Build new modes string
        let newModes = this._modes.split('');
        newModes[pin - 1] = newMode;
        newModes = newModes.join('');

        if (!serial.connected) {
            this._modes = newModes;
            return;
        }

        try {
            const actualModes = await serial.setModes(newModes);
            if (actualModes && actualModes.length === 10) {
                this._modes = actualModes;
                this._updateModeSelects();
                // Clear value for this pin after the device accepted the mode update.
                this._updateValue(pin, '-');
            } else {
                // Revert
                const select = this._container.querySelector(`select[data-pin="${pin}"]`);
                select.value = this._modes[pin - 1];
                notifications.error(t('command-error'));
            }
        } catch (error) {
            const select = this._container.querySelector(`select[data-pin="${pin}"]`);
            if (select) {
                select.value = this._modes[pin - 1];
            }
            notifications.error(error.message);
        }
    }

    async _handleSetValue(pin) {
        if (!serial.connected) {
            notifications.warning(t('disconnected'));
            return;
        }

        const row = this._container.querySelector(`tr[data-pin="${pin}"]`);
        const input = row.querySelector('.value-input');
        const mode = this._modes[pin - 1];

        // Check if this mode supports setting values
        if (!this._modeSupportsValueSetting(mode)) {
            notifications.warning(t('mode-not-settable'));
            return;
        }

        // If input is hidden, show it and return (user needs to enter value first)
        if (input.classList.contains('hidden')) {
            this._showValueInput(pin);
            return;
        }

        const value = input.value.trim();

        // Validate and send
        await this._sendPinValue(pin, value);
    }

    async _sendPinValue(pin, value) {
        if (!serial.connected) {
            notifications.warning(t('disconnected'));
            return;
        }

        const mode = this._modes[pin - 1];

        let normalizedValue = value;
        let pwmFrequency = null;
        try {
            if (mode === 'P') {
                normalizedValue = String(validatePwmDuty(value));
                pwmFrequency = validatePwmFrequency(this._pwmFrequencyInput?.value ?? '');
            } else if (!this._validateValue(mode, value)) {
                throw new Error(t('invalid-value'));
            }
        } catch (error) {
            notifications.error(error.message || t('invalid-value'));
            this._hideValueInput(pin);
            return;
        }

        try {
            const success = mode === 'P'
                ? await serial.setPWM(pin, normalizedValue, pwmFrequency)
                : await serial.setPin(pin, normalizedValue);
            if (success) {
                this._updateValue(pin, normalizedValue);
                this._hideValueInput(pin);
            } else {
                notifications.error(t('command-error'));
                this._hideValueInput(pin);
            }
        } catch (error) {
            notifications.error(error.message);
            this._hideValueInput(pin);
        }
    }

    async _handleReadValue(pin) {
        if (!serial.connected) {
            notifications.warning(t('disconnected'));
            return;
        }

        try {
            const value = await serial.readPin(pin);
            if (value !== null && value !== undefined) {
                this._applyReadValue(pin, value);
            }
        } catch (error) {
            if (error.message === 'Timeout') {
                notifications.warning(`Pin ${pin}: ${t('timeout')}`);
            } else {
                notifications.error(error.message);
            }
        }
    }

    _validateValue(mode, value) {
        switch (mode) {
            case 'O':
                return value === '0' || value === '1';
            case 'P':
                try {
                    validatePwmDuty(value);
                    return true;
                } catch {
                    return false;
                }
            case 'T':
                return value.length > 0;
            default:
                return true;
        }
    }

    _modeSupportsValueSetting(mode) {
        return ['O', 'P', 'T'].includes(mode);
    }

    _updateValue(pin, value) {
        this._values[pin] = value;
        const display = this._container.querySelector(`.value-display[data-pin="${pin}"]`);
        if (display) {
            display.textContent = value;
        }
    }

    async loadModes() {
        if (!serial.connected) return;

        try {
            const modes = await serial.getModes();
            if (modes && modes.length === 10) {
                this._modes = modes;
                this._updateModeSelects();
            }
        } catch (error) {
            notifications.error(error.message);
        }
    }

    async loadValues() {
        if (!serial.connected) return;

        try {
            const valuesStr = await serial.getValues();
            if (valuesStr?.length === 10) {
                for (let pin = 1; pin <= 10; pin++) {
                    const mode = this._modes[pin - 1];
                    if (mode === 'I' || mode === 'O') {
                        this._updateValue(pin, valuesStr[pin - 1]);
                    } else if (!(pin in this._values)) {
                        this._updateValue(pin, '-');
                    }
                }
            }

            // V is only a one-digit summary. PWM duty and ADC must be read
            // individually to avoid displaying 0 and 0..9 as exact values.
            for (let pin = 1; pin <= 10 && serial.connected; pin++) {
                const mode = this._modes[pin - 1];
                if (mode !== 'P' && mode !== 'A') continue;

                try {
                    const value = await serial.readPin(pin);
                    this._applyReadValue(pin, value);
                } catch (error) {
                    if (!serial.connected) break;
                    console.warn(`Failed to read pin ${pin}:`, error.message);
                }
            }
        } catch (error) {
            console.warn('Failed to load values:', error.message);
        }
    }

    _updateModeSelects() {
        for (let pin = 1; pin <= 10; pin++) {
            const select = this._container.querySelector(`select[data-pin="${pin}"]`);
            if (select) {
                const mode = this._modes[pin - 1];
                // Check if mode is available for this pin
                const availableModes = this._getAvailableModes(pin);
                if (availableModes.includes(mode)) {
                    select.value = mode;
                } else {
                    // If the mode from device is not available for this pin,
                    // log a warning but keep the current UI selection
                    console.warn(`Pin ${pin}: mode '${mode}' not available, available modes:`, availableModes);
                }
            }
        }
    }

    async readAllPins() {
        if (!serial.connected) {
            notifications.warning(t('disconnected'));
            return;
        }

        for (let pin = 1; pin <= 10; pin++) {
            try {
                const value = await serial.readPin(pin);
                if (value !== null && value !== undefined) {
                    this._applyReadValue(pin, value);
                }
            } catch (error) {
                this._updateValue(pin, 'ERR');
                if (!serial.connected) break;
            }
            // Small delay between reads
            await new Promise(r => setTimeout(r, 50));
        }
    }

    _applyReadValue(pin, value) {
        const mode = this._modes[pin - 1];
        const text = String(value ?? '').trim();

        if (mode === 'P') {
            try {
                this._updateValue(pin, String(validatePwmDuty(text)));
                return true;
            } catch {
                // Firmware 1.3.0 cannot read PWM duty. Keep the last value
                // confirmed by this client instead of replacing it with 0/error.
                if (!(pin in this._values)) this._updateValue(pin, '-');
                return false;
            }
        }

        if (mode === 'A') {
            if (!/^\d+$/.test(text)) return false;
            const numericValue = Number(text);
            if (!Number.isInteger(numericValue) || numericValue < 0 || numericValue > 4095) {
                return false;
            }
        }

        this._updateValue(pin, text || '-');
        return true;
    }

    async saveToFlash() {
        if (!serial.connected) {
            notifications.warning(t('disconnected'));
            return;
        }

        try {
            const success = await serial.saveToFlash();
            if (success) {
                notifications.success(t('saved-to-flash'));
            } else {
                notifications.error(t('flash-save-error'));
            }
        } catch (error) {
            notifications.error(error.message);
        }
    }

    get modes() {
        return this._modes;
    }

    get values() {
        return this._values;
    }
}
